import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Compass } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=2400&q=85";

export default function Solutions() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/solutions").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-[color:var(--mia-navy)] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Waterfront" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--mia-navy)] via-[color:var(--mia-navy)]/85 to-[color:var(--mia-navy)]/25" />
        </div>
        <div className="mia-container py-14 lg:py-12 lg:py-20 relative">
          <div className="text-[0.72rem] font-bold tracking-[0.24em] uppercase text-[color:var(--mia-green)]">Our Solutions</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02] tracking-tight max-w-3xl">
            Innovative Marine<br />
            Infrastructure Solutions<br />
            Built for <span className="text-[color:var(--mia-green)]">Africa.</span>
          </h1>
          <div className="w-16 h-1 bg-[color:var(--mia-green)] my-7" />
          <p className="text-white/85 max-w-lg leading-relaxed">
            We deliver end-to-end marine and waterfront infrastructure solutions that are engineered for
            performance, built for safety and designed to create long-term value.
          </p>
        </div>
      </section>

      {/* Intro strip */}
      <section className="py-14 bg-white">
        <div className="mia-container flex gap-6 items-start max-w-4xl">
          <div className="icon-round-light shrink-0"><Compass className="w-7 h-7" strokeWidth={1.6} /></div>
          <div>
            <div className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-tight">End-to-end. Built around your vision.</div>
            <p className="text-[color:var(--mia-muted)] mt-3 leading-relaxed">
              From concept to completion, we provide integrated solutions across marine infrastructure, floating
              systems, waterfront development and marine access solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Solution image cards */}
      <section className="pb-24 bg-white" data-testid="solutions-grid">
        <div className="mia-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((s) => (
            <div key={s.id} className="bg-white rounded-md overflow-hidden border border-slate-100 card-lift flex flex-col" data-testid={`solutions-card-${s.slug}`}>
              <div className="relative aspect-square overflow-hidden bg-white">
                <img src={s.image} alt={s.title} className="w-full h-full object-contain" />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display font-bold text-[color:var(--mia-navy)] text-[0.95rem] tracking-[0.08em] uppercase leading-tight">{s.title}</h3>
                <p className="text-sm text-[color:var(--mia-muted)] mt-3 leading-relaxed flex-1">{s.tagline}</p>
                <Link to={`/solutions/${s.slug}`} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--mia-navy)] hover:text-[color:var(--mia-green)]" data-testid={`learn-more-${s.slug}`}>
                  Learn More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-14 lg:pb-16 bg-white">
        <div className="mia-container">
          <div className="bg-[color:var(--mia-navy)] rounded-md p-6 sm:p-8 lg:p-10 flex flex-col lg:flex-row gap-6 items-start lg:items-center text-white">
            <div className="w-14 h-14 rounded-full bg-white/10 text-[color:var(--mia-green)] flex items-center justify-center shrink-0">
              <Compass className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-xl sm:text-2xl leading-tight">Let's Build Africa's Next Waterfront Together.</div>
              <div className="text-sm text-white/70 mt-2">Partner with us for innovative, sustainable and future-ready marine infrastructure solutions.</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <Link to="/request-consultation" className="mia-btn mia-btn-white w-full sm:w-auto" data-testid="sol-cta-consultation">
                Request Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="mia-btn mia-btn-outline-white w-full sm:w-auto" data-testid="sol-cta-proposal">
                Get A Proposal <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
